import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../contexts/OnboardingContext';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Rocket, Wallet, Users, FileText, Send, BarChart3, ArrowRight, ArrowLeft } from 'lucide-react';

const GUIDE_STEPS = [
  {
    icon: Rocket,
    title: 'Welcome to Dazza',
    description: 'Dazza makes it easy to send SMS marketing campaigns to your customers. Follow these steps to get up and running in minutes.',
    color: 'orange',
  },
  {
    icon: Wallet,
    title: 'Top Up Your Wallet',
    description: 'Dazza uses a prepaid wallet model — buy SMS credits and use them when you send. You start with $5 free credit to try things out.',
    cta: { label: 'Go to Wallet', href: '/dashboard/wallet' },
    color: 'green',
  },
  {
    icon: Users,
    title: 'Import Your Customers',
    description: 'Upload a CSV of your customer list or add them manually. Dazza auto-deduplicates contacts and normalises Australian phone numbers.',
    cta: { label: 'Go to Customers', href: '/dashboard/customers' },
    color: 'blue',
  },
  {
    icon: FileText,
    title: 'Create SMS Templates',
    description: 'Build reusable message templates for your campaigns. Try the AI writer to generate professional SMS copy in seconds.',
    cta: { label: 'Go to Templates', href: '/dashboard/templates' },
    color: 'purple',
  },
  {
    icon: Send,
    title: 'Send Your First Campaign',
    description: 'Pick a template, choose your audience or segment, and schedule or send immediately. Track delivery in real time.',
    cta: { label: 'Go to Campaigns', href: '/dashboard/campaigns' },
    color: 'orange',
  },
  {
    icon: BarChart3,
    title: 'Track Your Results',
    description: 'Monitor delivery rates, campaign performance, and ROI attribution from the analytics dashboard.',
    cta: { label: 'Go to Analytics', href: '/dashboard/analytics' },
    color: 'emerald',
  },
];

const COLOR_MAP = {
  orange: { bg: 'bg-orange-100', text: 'text-orange-600', dot: 'bg-orange-500' },
  green: { bg: 'bg-green-100', text: 'text-green-600', dot: 'bg-green-500' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600', dot: 'bg-blue-500' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600', dot: 'bg-purple-500' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', dot: 'bg-emerald-500' },
};

export default function GuideModal() {
  const { guideOpen, closeGuide } = useOnboarding();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const step = GUIDE_STEPS[currentStep];
  const Icon = step.icon;
  const colors = COLOR_MAP[step.color];
  const isFirst = currentStep === 0;
  const isLast = currentStep === GUIDE_STEPS.length - 1;

  const handleClose = () => {
    closeGuide();
    setCurrentStep(0);
  };

  const handleCta = (href) => {
    handleClose();
    navigate(href);
  };

  return (
    <Dialog open={guideOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle className="sr-only">Dazza Guide</DialogTitle>

        {/* Step indicator dots */}
        <div className="flex items-center justify-center gap-2 mb-2">
          {GUIDE_STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentStep(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                i === currentStep ? `${colors.dot} scale-125` : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to step ${i + 1}`}
            />
          ))}
        </div>

        {/* Icon + Content */}
        <div className="text-center py-4">
          <div className={`w-16 h-16 ${colors.bg} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
            <Icon className={`w-8 h-8 ${colors.text}`} />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
          <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
        </div>

        {/* CTA button */}
        {step.cta && (
          <button
            onClick={() => handleCta(step.cta.href)}
            className="w-full py-2.5 px-4 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {step.cta.label}
          </button>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={() => setCurrentStep(s => s - 1)}
            disabled={isFirst}
            className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${
              isFirst ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {isLast ? (
            <button
              onClick={handleClose}
              className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg text-orange-600 hover:bg-orange-50 transition-colors"
            >
              Done
            </button>
          ) : (
            <button
              onClick={() => setCurrentStep(s => s + 1)}
              className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg text-orange-600 hover:bg-orange-50 transition-colors"
            >
              Next <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
